import { useStateWithRef } from '@/vantage/hooks';
import { spinner } from '@/vantage/reusedComponents';
import {
  capsuleContextId,
  constructKey,
  ensureColumnVisible,
} from '@/vantage/components/evidence_table/helpers/utilities';
import type {
  CellClickedEvent,
  ColDef,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetRowIdParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ModelUpdatedEvent,
  RowNode,
  SelectionChangedEvent,
} from 'ag-grid-community';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  generateAutoGroupColumnDef,
  generateColumnsDef,
} from '@/vantage/components/evidence_table/helpers/columnDefGenerator';
import { BooleanColumnRenderer } from '@/vantage/components/evidence_table/helpers/CustomColumnRenderers';
import { quickFilterParser } from '@/vantage/components/evidence_table/helpers/filterModels';
import { errorToast } from '@/utilities/toast.utilities';
import { AgGridAsync } from '@/core/tableUtilities/AgGridAsync';
import DateColumnFilter from '@/vantage/components/evidence_table/helpers/DateColumnFilter';
import { VantageNames } from '@/vantage/constants';
import { EvidenceLimitWarning } from '@/vantage/components/evidence_table/EvidenceLimitWarning';
import {
  applySerializedGridState,
  baseGridOptions,
  defaultColDef,
  getColumnContexts,
  getRowClassRules,
  modules,
  numericColumnDecimals,
  transitionSelectionOnGrouping,
} from '@/vantage/components/evidence_table/evidenceTable.utilities';
import { EvidenceTableContainer } from '@/vantage/components/evidence_table/EvidenceTableContainer';
import type { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import type { TableColumns } from '@/vantage/types/TableConfig.types';
import {
  findConditionIdColumnNames,
  findConditions,
} from '@/vantage/components/evidence_table/helpers/conditionFinders';
import type { ContextSchema } from '@/vantage/types/Context.types';
import { aggFuncsGenerator } from './helpers/aggregators';
import { tableConfig } from '@/vantage/setup';

export interface EvidenceTableContentProps {
  contentId: string;
  roomName: string;
  viewName: string;
  quickFilter: string;
  tableId: string;
  tableColumns: TableColumns;
  tableData: Record<string, any>[];
  evidenceContext: ContextSchema[];
  nonEvidenceContext: ContextSchema[];
  timezone: string;
  gridState: Record<string, any>;
}

const CustomLoadingOverlay = (props: { loadingMessage: string }) => {
  return (
    <div className="flexFill" style={{ backgroundColor: 'transparent' }}>
      {spinner(props.loadingMessage)}
    </div>
  );
};

const getContextByColumn = (
  tableColumns: TableColumns,
  evidenceContext: ContextSchema[],
  nonEvidenceContext: ContextSchema[],
) => {
  const ctx: Record<string, string[]> = {};
  if (!tableColumns?.extensions || !evidenceContext || !nonEvidenceContext) {
    return ctx;
  }

  Object.entries({
    ...tableColumns.extensions,
    [VantageNames.Columns.Suppressed]: tableColumns.base?.[VantageNames.Columns.Suppressed],
  }).forEach(([columnName, config]) => {
    const columnConfig = config?.meta;
    if (columnConfig?.colDefFilterType === 'set') {
      ctx[columnName] =
        columnConfig?.discreteStates?.length && columnConfig.discreteStates.length > 0
          ? columnConfig.discreteStates.map((state) => state.value)
          : getColumnContexts(columnConfig, evidenceContext, nonEvidenceContext);
    }
  });
  return ctx;
};

const getEvidenceContextMap = (evidenceContext: ContextSchema[]) => {
  const map = new Map();
  evidenceContext.forEach((ctx) => {
    const key = constructKey(capsuleContextId(ctx), ctx.identifier);
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(ctx);
  });
  return map;
};

const getRowId = (params: GetRowIdParams) => `${params.data?.[VantageNames.Columns.CapsuleID]}-${params.level ?? 0}`;

export const EvidenceTableContent: FC<EvidenceTableContentProps> = ({
  contentId,
  roomName,
  viewName,
  quickFilter,
  tableId,
  tableColumns,
  tableData,
  evidenceContext = [],
  nonEvidenceContext = [],
  gridState,
  timezone,
}) => {
  const { t } = useTranslation();
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilterValue, setQuickFilterValue] = useState(quickFilter);
  const autoGroupColumnDef = useRef<ColDef>(generateAutoGroupColumnDef([]));

  const onGridReady = useCallback(({ api }: GridReadyEvent) => {
    setGridApi(api);
  }, []);

  const signalRenderingIsDone = useCallback((event: FirstDataRenderedEvent | ModelUpdatedEvent) => {
    // This tells the Seeq renderer that the grid has loaded data so it finishes the content loading process
    if (
      event.type === 'firstDataRendered' ||
      (event.type === 'modelUpdated' && event.api.getDisplayedRowCount() === 0)
    ) {
      gridWrapperRef.current?.classList.remove('agGridRendering');
    }
  }, []);

  useEffect(
    () => {
      if (gridApi) {
        // Set the table columns
        const conditionsList = findConditions(tableColumns, findConditionIdColumnNames(tableColumns));
        autoGroupColumnDef.current = generateAutoGroupColumnDef(conditionsList);
        const numericColumns = { current: numericColumnDecimals(tableData, tableColumns) };
        const evidenceContextMap = { current: getEvidenceContextMap(evidenceContext) };
        const contextByColumn = { current: getContextByColumn(tableColumns, evidenceContext, nonEvidenceContext) };
        const columnDefs = generateColumnsDef(
          tableColumns,
          numericColumns,
          conditionsList,
          evidenceContextMap,
          contextByColumn,
          tableId,
          timezone,
          t,
        );
        gridApi.setGridOption('columnDefs', columnDefs);

        // Set the Aggregators
        const nonEvidenceContextMapRef = { current: getEvidenceContextMap(nonEvidenceContext) };
        const contextItemIdMapping = tableConfig().contextItemIdMapping;
        const aggregators = aggFuncsGenerator(tableColumns.extensions!, nonEvidenceContextMapRef, contextItemIdMapping);
        gridApi.addAggFuncs(aggregators);

        // Apply the saved grid state only once
        applySerializedGridState(gridApi, gridState, quickFilterValue, null);

        // Set the table data
        gridApi.setGridOption('rowData', tableData);

        // Apply the saved state
        gridApi.refreshClientSideRowModel('filter');
      }
    },
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps -- leaving out quickFilterValue to avoid resetting the filter on each change
    [gridApi, t, tableId, tableColumns, tableData, evidenceContext, nonEvidenceContext, timezone, gridState],
  );

  const [rawSelectedNodes, setRawSelectedNodes] = useStateWithRef<IRowNode<any>[]>([]);

  const lastClickedNode = useRef<RowNode | null>(null);
  const lastSelectedNodes = useRef<RowNode[]>([]);

  const onSelectionChangedHandler = (event: SelectionChangedEvent) => {
    const rawSelectedNodes: IRowNode<any>[] = event.api.getSelectedNodes();
    if (!rawSelectedNodes.length) {
      return;
    }
    setRawSelectedNodes(rawSelectedNodes);
  };

  const onFilterChangedHandler = (event: FilterChangedEvent<any, any>) => {
    event.api.refreshClientSideRowModel('aggregate');
    onSelectionChangedHandler(event as unknown as SelectionChangedEvent<any, any>);
  };

  function handleGroupedClick(currentNode: RowNode, api: GridApi, isShiftClick: boolean): void {
    if (!lastClickedNode.current) return;

    if (lastClickedNode.current.level !== currentNode.level) {
      errorToast({ messageKey: t('VANTAGE.EVIDENCE_TABLE.ERROR.GROUPING_LEVEL') });
      api.deselectAll();
      lastSelectedNodes.current.forEach((node) => node.setSelected(true));
      return;
    }

    if (isShiftClick) {
      const validShiftClickGroup = api
        .getSelectedNodes()
        .filter((node) => node.level === lastClickedNode.current?.level);
      api.deselectAll();
      validShiftClickGroup.forEach((node) => node.setSelected(true));
    }

    lastClickedNode.current = currentNode;
    lastSelectedNodes.current = api.getSelectedNodes() as RowNode[];
  }

  const gridOptions: GridOptions = {
    ...baseGridOptions,
    skipHeaderOnAutoSize: true,
    autoSizePadding: 10,
    onColumnVisible: (event) => {
      event.api.setGridOption(
        'columnDefs',
        ensureColumnVisible(event.api.getColumnDefs() as { headerName?: string; hide: boolean }[]),
      );
    },
    onFilterChanged: onFilterChangedHandler,
    onSelectionChanged: onSelectionChangedHandler,
    onCellClicked: (event: CellClickedEvent) => {
      const mouse_event = event.event as MouseEvent;
      if (mouse_event.shiftKey) {
        handleGroupedClick(event.node as RowNode, event.api, true);
      } else if (mouse_event.ctrlKey || mouse_event.metaKey) {
        handleGroupedClick(event.node as RowNode, event.api, false);
      } else {
        lastClickedNode.current = event.node as RowNode;
        lastSelectedNodes.current = event.api.getSelectedNodes() as RowNode[];
      }
    },
    rowClassRules: getRowClassRules({}),
    components: {
      dateColumnFilter: DateColumnFilter,
      customColumnRenderer: BooleanColumnRenderer,
    },
    quickFilterParser,
    onColumnRowGroupChanged(event) {
      transitionSelectionOnGrouping(event, rawSelectedNodes.current);
    },
  };

  const loadingOverlayComponentParams = useMemo(
    () => ({
      loadingMessage: t('VANTAGE.EVIDENCE_TABLE.LOADING'),
    }),
    [t],
  );

  return (
    <EvidenceTableContainer
      quickFilter={quickFilterValue}
      onQuickFilterChange={setQuickFilterValue}
      gridApi={gridApi}
      gridRef={gridRef}
      roomName={roomName}
      viewName={viewName}>
      <div
        id={`vantageGrid-content-${contentId}`}
        ref={gridWrapperRef}
        className="ag-theme-quartz ag-striped flexFillOverflow overflowAuto displayGrid vantageAgGrid agGridRendering">
        <AgGridAsync
          modules={modules}
          ref={gridRef}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef.current}
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          onFirstDataRendered={signalRenderingIsDone}
          onModelUpdated={signalRenderingIsDone}
          getRowId={getRowId}
          reactiveCustomComponents
          loadingOverlayComponent={CustomLoadingOverlay}
          loadingOverlayComponentParams={loadingOverlayComponentParams}
          autoSizeStrategy={undefined} // by default the grid sets this to 'fitGridWidth' internally, which is not what we want
        />
      </div>
      <EvidenceLimitWarning evidenceCount={tableData?.length} />
    </EvidenceTableContainer>
  );
};
